// Estilo propio del mapa base (opción `styles`; sólo funciona porque el mapa
// es ráster, sin mapId). Inspección del 6-oct: en tema oscuro el mapa seguía
// claro y los íconos de restaurantes y tiendas competían con las unidades.

type MapTypeId = "roadmap" | "satellite" | "hybrid" | "terrain";

// Negocios y paradas de autobús: ya sin negocios, en la Macroplaza de Monterrey
// (zoom de seguimiento) quedaban 7 paradas alrededor de una sola unidad.
// Parques, hospitales, museos y edificios de gobierno siguen: sirven de referencia.
const SIN_NEGOCIOS: google.maps.MapTypeStyle[] = [
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit.station.bus", stylers: [{ visibility: "off" }] },
];

// La paleta oscura sale del tema del tenant, igual que la de tailwind.config.ts
// (VITE_THEME, si no grayscale). Antes era la escala gray de Tailwind, que tira a
// azul (#1f2937): junto al panel gris neutro de Dumax (#2B2B2B) el mapa se veía
// de otra app (Daniel, 27-oct).
type Paleta = { brand: { primary: string }; colors: { dark: Record<"bg" | "surface" | "card" | "border" | "hover", string> } };
const TEMAS = import.meta.glob<Paleta>("../../config/themes/[a-z]*.json", { eager: true, import: "default" });
/** Paleta del tenant; la vista de manejo (MapLibre) la usa para verse igual que este mapa. */
export const tema = (nombre?: string) =>
  TEMAS[`../../config/themes/${nombre}.json`] ?? TEMAS["../../config/themes/grayscale.json"];

/** Mezcla dos #rrggbb: t=0 es `a`, t=1 es `b`. */
export function mezcla(a: string, b: string, t: number): string {
  const c = (h: string, i: number) => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  return "#" + [0, 1, 2].map((i) => Math.round(c(a, i) + (c(b, i) - c(a, i)) * t).toString(16).padStart(2, "0")).join("");
}

export function estiloOscuro(nombreTema?: string): google.maps.MapTypeStyle[] {
  const { brand, colors: { dark: d } } = tema(nombreTema);
  const texto = mezcla(d.hover, "#ffffff", 0.45);
  return [
    { elementType: "geometry", stylers: [{ color: d.surface }] },
    { elementType: "labels.text.stroke", stylers: [{ color: d.bg }] },
    { elementType: "labels.text.fill", stylers: [{ color: texto }] },
    { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: d.border }] },
    { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: mezcla(d.surface, d.bg, 0.3) }] },
    { featureType: "poi", elementType: "geometry", stylers: [{ color: mezcla(d.surface, d.card, 0.5) }] },
    // Un toque del color de la marca en los parques: referencia sin competir con las unidades.
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: mezcla(d.surface, brand.primary, 0.12) }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: d.card }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: d.surface }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: d.border }] },
    { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: mezcla(d.hover, "#ffffff", 0.7) }] },
    { featureType: "transit", elementType: "geometry", stylers: [{ color: d.card }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: d.bg }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: d.hover }] },
  ];
}

const OSCURO = estiloOscuro(import.meta.env.VITE_THEME);

/** Satélite e híbrido conservan su foto: sólo se quitan los negocios. */
export function mapBaseStyles(isDarkMode: boolean, mapType: MapTypeId): google.maps.MapTypeStyle[] {
  const vector = mapType === "roadmap" || mapType === "terrain";
  return isDarkMode && vector ? [...OSCURO, ...SIN_NEGOCIOS] : SIN_NEGOCIOS;
}
